import React, {useState} from "react";
import {FormControlLabel, Switch, Tab, Tabs, Typography} from "@mui/material";
import {useHistory, useParams} from "react-router-dom";
import {LoadingButton} from "@mui/lab";
import {useRecoilValue} from "recoil";
import {userRolesState} from "../../../State/user.Atom";
import {EE123SalariesRole_Edit, EE123SalariesRole_View} from "../../../Component/Common/Constants/UserRoles";
import {RequestTrackerService} from "../../../Component/Api/Staging/RequestTrackerService";
import {StagedDataTable} from "./StagedDataTable";
import {
    StagedIndividualTableMapper,
    StagedIndividualEmployeeDataTableMapper,
    StagedIndividualSalaryTableMapper
} from "./StagedDataTableMappers";

enum StagedSection {
    Individuals,
    IndividualEmployeeData,
    IndividualSalaries
}

export const PayrollStagedData = () => {
    const {requestId} = useParams<{ requestId: string }>();
    const history = useHistory();
    const userRoles = useRecoilValue(userRolesState);

    const [section, setSection] = useState<StagedSection>(StagedSection.Individuals);
    const [failingOnly, setFailingOnly] = useState<boolean>(false);

    const requestIdNumber = Number(requestId);

    // Amounts appear nowhere else in the payroll screens; the API enforces the same rule.
    const canViewSalaries = userRoles.some(r => r === EE123SalariesRole_View || r === EE123SalariesRole_Edit);

    return (
        <>
            <LoadingButton onClick={() => history.push("/organisation/payrollData/fetchData")} sx={{mb: 2}}>
                &larr; Back to requests
            </LoadingButton>

            <Typography variant="h4" fontWeight="bold" style={{paddingBottom: "10px"}}>
                Staged Data
            </Typography>
            <Typography variant="body2" color="secondary" mb={2}>
                What the fetch put into staging, before any of it is imported. Search part of a name or employee
                code, or a whole ID number. Staged data is kept for a week after the fetch.
            </Typography>

            <Tabs value={section} onChange={(_, value) => setSection(value)} sx={{mb: 2}}>
                <Tab label="Employee Structure" value={StagedSection.Individuals}/>
                <Tab label="Employment Records" value={StagedSection.IndividualEmployeeData}/>
                {canViewSalaries && <Tab label="Employee Salaries" value={StagedSection.IndividualSalaries}/>}
            </Tabs>

            <FormControlLabel
                control={<Switch checked={failingOnly} onChange={(_, checked) => setFailingOnly(checked)}/>}
                label="Only rows that failed validation"
                sx={{mb: 1}}
            />

            {section === StagedSection.Individuals &&
                <StagedDataTable
                    requestId={requestIdNumber}
                    failingOnly={failingOnly}
                    search={RequestTrackerService.searchStagedIndividuals}
                    mapper={StagedIndividualTableMapper}
                    noDataMessage="No staged individuals."
                />
            }

            {section === StagedSection.IndividualEmployeeData &&
                <StagedDataTable
                    requestId={requestIdNumber}
                    failingOnly={failingOnly}
                    search={RequestTrackerService.searchStagedIndividualEmployeeData}
                    mapper={StagedIndividualEmployeeDataTableMapper}
                    noDataMessage="No staged employee data records."
                />
            }

            {section === StagedSection.IndividualSalaries && canViewSalaries &&
                <StagedDataTable
                    requestId={requestIdNumber}
                    failingOnly={failingOnly}
                    search={RequestTrackerService.searchStagedIndividualSalaries}
                    mapper={StagedIndividualSalaryTableMapper}
                    noDataMessage="No staged salaries."
                />
            }
        </>
    );
};
